import { Clock, ExternalLink, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AsyncSelect } from "@/components/ui/async-select";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { usePersonDetail } from "@/hooks/api/use-person-detail";
import { type Person, useInfinitePersons } from "@/hooks/api/use-persons";
import {
  type RecentPerson,
  usePersonSelection,
} from "@/hooks/use-person-selection";

// Union type to handle both full persons and recent persons
type PersonOption = Person | RecentPerson;

// Helper to determine if an option is a recent person (has timestamp)
const isRecentPerson = (person: PersonOption): person is RecentPerson => {
  return "timestamp" in person;
};

// Helper to format person name
const formatPersonName = (person: PersonOption) => {
  const name = [person.first_name, person.last_name].filter(Boolean).join(" ");
  return name || person.email_address || "Unknown";
};

/**
 * Renders a person option with name, email, and related accounts
 */
const PersonOptionContent = ({ person }: { person: PersonOption }) => {
  // Lazily fetch person details for account information
  // Fetch for all persons that don't already have accounts loaded
  const shouldFetch = !(person as Person).accounts;
  const { data: personDetail } = usePersonDetail(
    shouldFetch ? person.id : undefined,
  );

  const accounts = (person as Person).accounts || personDetail?.accounts || [];

  return (
    <div className="flex items-center justify-between w-full group py-1">
      <div className="flex items-center gap-2 flex-1 min-w-0">
        {isRecentPerson(person) && (
          <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
        )}
        <div className="flex flex-col min-w-0 flex-1 gap-1">
          <div className="font-medium truncate">{formatPersonName(person)}</div>
          <div className="text-xs text-muted-foreground">
            {person.email_address && (
              <div className="truncate">{person.email_address}</div>
            )}
            {accounts.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1">
                {accounts.slice(0, 3).map((account) => (
                  <Badge
                    key={account.id}
                    variant="default"
                    className="text-xs px-1.5 py-0.5 font-normal normal-case"
                  >
                    {account.name}
                  </Badge>
                ))}
                {accounts.length > 3 && (
                  <Badge
                    variant="default"
                    className="text-xs px-1.5 py-0.5 font-normal normal-case"
                  >
                    +{accounts.length - 3}
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Tooltip>
        <TooltipTrigger asChild>
          <a
            href={`/persons/${person.id}/explorer`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              window.open(`/persons/${person.id}/explorer`, "_blank");
            }}
            className="ml-2"
          >
            <ExternalLink className="h-4 w-4 text-muted-foreground hover:text-foreground cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity" />
          </a>
        </TooltipTrigger>
        <TooltipContent side="left" sideOffset={5}>
          <p>Open in new tab</p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

/**
 * Person selector component
 *
 * Allows searching and selecting persons with a dropdown.
 * Shows recent persons and search results.
 */
export function PersonSelector() {
  const [search, setSearch] = useState<string>();
  const { selectedPersonId, initialPerson, handlePersonChange, recentPersons } =
    usePersonSelection();

  // Fetch persons based on search using infinite query
  const {
    data: searchData,
    hasNextPage,
    isFetching,
  } = useInfinitePersons(
    search
      ? {
          search,
          limit: 50,
        }
      : undefined,
  );

  // Fetch selected person details if not found in recent persons
  const { data: personDetail } = usePersonDetail(
    !initialPerson && selectedPersonId ? selectedPersonId : undefined,
  );

  // Determine which person to show as selected
  const selectedPerson = initialPerson ?? personDetail;

  // Add the selected person to recent persons when coming from a URL
  useEffect(() => {
    if (!initialPerson && selectedPersonId && selectedPerson) {
      handlePersonChange(selectedPerson.id, selectedPerson);
    }
  }, [initialPerson, selectedPersonId, selectedPerson, handlePersonChange]);

  // Flatten search results from infinite query
  const searchResults = useMemo(() => {
    if (!searchData?.pages) return [];
    return searchData.pages.flatMap((page) => page.results);
  }, [searchData]);

  // Fetch handler for AsyncSelect component
  const fetcher = useCallback(
    async (
      searchValue?: string,
    ): Promise<{ results: PersonOption[]; hasMore: boolean }> => {
      // Update search value if it changed
      if (searchValue !== search) {
        setSearch(searchValue || undefined);
      }

      // Get search results if available
      if (searchValue && searchResults.length > 0) {
        // Show search results
        return {
          results: searchResults.slice(0, 10), // Cap at 10 for performance
          hasMore: hasNextPage || false,
        };
      }

      // If we're searching but don't have results yet, show loading
      // Return empty array while fetching to trigger the "notFound" state
      if (searchValue && isFetching) {
        return {
          results: [],
          hasMore: false,
        };
      }

      // Default to showing recent persons
      return {
        results: recentPersons,
        hasMore: false,
      };
    },
    [search, searchResults, recentPersons, isFetching, hasNextPage],
  );

  return (
    <AsyncSelect<PersonOption>
      fetcher={fetcher}
      preload={true}
      renderOption={(person) => <PersonOptionContent person={person} />}
      getOptionValue={(person) => person.id}
      getDisplayValue={formatPersonName}
      selectedOption={selectedPerson}
      label="Person"
      placeholder="Select person..."
      searchPlaceholder="Search by name, email, or source record ID"
      value={selectedPersonId ?? ""}
      onChange={handlePersonChange}
      isSearching={!!search && isFetching}
      triggerClassName="bg-white dark:bg-[hsl(195_30%_12%)]"
      notFound={
        <div className="py-6 text-center text-sm">
          {isFetching ? (
            <div className="flex items-center justify-center space-x-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Searching persons...</span>
            </div>
          ) : search ? (
            "No persons found"
          ) : (
            "No recent persons"
          )}
        </div>
      }
      loadingSkeleton={
        <div className="p-4">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-4">
                <div className="h-4 w-4/5 animate-pulse rounded-lg corner-smooth-lg bg-muted" />
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
}
